import {Injectable} from '@angular/core';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import {SpatialEntityDataExtended} from '@core/utils/engin/geospatial-viewer/helper/engin-data-prepare.service';
import {VisualizationType} from '../../../../../../pages/geospatial-viewer/model/visualization';
import {LayerSettings} from '@core/utils/engin/geospatial-viewer/esri-layers/generate-layer.service';
import {GenericPopupService} from '@core/utils/engin/geospatial-viewer/popups/generic-popup.service';
import {AssetRendererService} from '@core/utils/engin/geospatial-viewer/renderers/asset-renderer.service';
import {AssetFieldsService} from '@core/utils/engin/geospatial-viewer/fields/asset-fields.service';
import Graphic from '@arcgis/core/Graphic';
import {GeospatialEntitySymbolType} from '../../../../../../pages/geospatial-viewer/model/api';
import Point from '@arcgis/core/geometry/Point';

@Injectable()
export class SymbolLayerService {
    constructor(
        private genericPopupService: GenericPopupService,
        private rendererService: AssetRendererService,
        private fieldsService: AssetFieldsService,
    ) {}

    /**
     * Create a point layers for symbol population:
     * 1. Point "symbol" layer for showing individual symbols
     * @return [FeatureLayer]
     */
    _preparePointSettings: LayerSettings = {
        showTooltip: true,
        suppressTooltipTitle: false,
        metrics: [],
        showStaticLabel: true,
        staticLabelProps: {
            expression: `if ($feature.count == 2)
                            {return Text('•')}`,
            labelPlacement: 'center-center',
            symbol: {
                type: 'text',
                color: 'black',
                font: {
                    weight: 'bold',
                    size: '12px',
                },
            },
        },
    };

    public prepareSymbolLayers(
        symbolData: SpatialEntityDataExtended[],
        renderedVisualizationType: VisualizationType,
        id: string,
        theme: string,
        symbolType: GeospatialEntitySymbolType,
        settings?: LayerSettings,
    ): FeatureLayer[] {
        // Get settings: priority of provided settings, else default settings i.e. allows settings to be partial set
        const finalSettings: LayerSettings = {
            ...this._preparePointSettings,
            ...settings,
        };
        let symbolPopupTemplate = null;
        if (finalSettings?.showTooltip) {
            symbolPopupTemplate = {
                title: finalSettings?.suppressTooltipTitle ? '' : '{id}',
                outFields: ['*'],
                content: this.genericPopupService.getGenericMetricTooltipFunction.bind(
                    this.genericPopupService,
                    finalSettings?.metrics || [],
                    renderedVisualizationType,
                ),
            };
        }
        const symbolSource: Graphic[] = this.createSymbolGraphics(symbolData, finalSettings?.metrics[0] || '');
        // Note: must be placed last in array to be displayed above other layers
        const substationSymbolLayer = new FeatureLayer({
            id: id ? id : 'symbol_layer',
            source: symbolSource,
            outFields: ['*'],
            renderer: this.rendererService.symbolRenderer(theme, symbolType),
            fields: this.fieldsService.getMapFields(),
            popupTemplate: symbolPopupTemplate,
        });

        return [substationSymbolLayer];
    }

    private createSymbolGraphics(symbolData: SpatialEntityDataExtended[], metric: string): Graphic[] {
        return symbolData.map((asset: SpatialEntityDataExtended) => {
            const point = new Point({
                latitude: asset.geoJsonGeometry.coordinates[1],
                longitude: asset.geoJsonGeometry.coordinates[0],
            });

            return new Graphic({
                geometry: point,
                attributes: {...asset, ...(!!metric ? {metric: metric} : {})},
            });
        });
    }
}
